import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Pencil, Trash2 } from "lucide-react";
import { Switch } from "../ui/switch";
import { api, errorMessage } from "../../lib/api";
import { Field, TextInput, TextArea } from "../forms/Fields";

const EMPTY = { quote: "", name: "", visible: true, order: 0 };

const Editor = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState(initial);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="bg-paper rounded-2xl p-6 space-y-5" aria-label="Testimonial" data-testid="testimonial-editor">
      <Field id="t-quote" label="Quote" required><TextArea id="t-quote" rows={4} required value={f.quote} onChange={(e) => setF({ ...f, quote: e.target.value })} /></Field>
      <div className="grid sm:grid-cols-[1fr_8rem] gap-5">
        <Field id="t-name" label="Name or role" required hint='e.g. "Annie S." or "Parent, Pittsburgh"'><TextInput id="t-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} aria-describedby="t-name-hint" /></Field>
        <Field id="t-order" label="Order"><TextInput id="t-order" type="number" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} /></Field>
      </div>
      <div className="flex items-center gap-3">
        <Switch id="t-visible" checked={f.visible} onCheckedChange={(v) => setF({ ...f, visible: v })} data-testid="t-visible-switch" />
        <label htmlFor="t-visible">Show on the website</label>
      </div>
      <div className="flex gap-3">
        <button type="submit" className="bg-teal text-paper rounded-full px-6 py-2.5 hover:bg-teal-deep" data-testid="testimonial-save-button">Save</button>
        {onCancel && <button type="button" onClick={onCancel} className="border border-ink/40 rounded-full px-5 py-2.5 hover:bg-sky" data-testid="testimonial-cancel-button">Cancel</button>}
      </div>
    </form>
  );
};

export const TestimonialsPanel = () => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // id or "new"
  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);

  const save = async (f) => {
    const body = { quote: f.quote, name: f.name, visible: f.visible, order: f.order || 0 };
    try {
      if (editing === "new") await api.post("/admin/testimonials", body); else await api.put(`/admin/testimonials/${editing}`, body);
      toast.success("Testimonial saved"); setEditing(null); load();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const toggle = async (t) => {
    try { await api.put(`/admin/testimonials/${t.id}`, { quote: t.quote, name: t.name, order: t.order, visible: !t.visible }); toast.success(t.visible ? "Hidden from the website" : "Showing on the website"); load(); } catch (e) { toast.error(errorMessage(e)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete the testimonial from ${t.name}?`)) return;
    try { await api.delete(`/admin/testimonials/${t.id}`); toast.success("Testimonial deleted"); load(); } catch (e) { toast.error(errorMessage(e)); }
  };

  if (!items) return <p className="font-sans text-slate">Loading...</p>;
  return (
    <div className="font-sans space-y-5" data-testid="admin-testimonials">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-slate">Visible testimonials appear on the Home page. Hidden ones stay saved here.</p>
        {editing !== "new" && <button onClick={() => setEditing("new")} className="bg-teal text-paper rounded-full px-5 py-2.5 hover:bg-teal-deep" data-testid="testimonial-add-button">Add testimonial</button>}
      </div>
      {editing === "new" && <Editor initial={EMPTY} onSave={save} onCancel={() => setEditing(null)} />}
      {items.length === 0 && editing !== "new" && <p className="text-slate">No testimonials yet.</p>}
      <ul className="space-y-4">
        {items.map((t) => editing === t.id ? (
          <li key={t.id}><Editor initial={t} onSave={save} onCancel={() => setEditing(null)} /></li>
        ) : (
          <li key={t.id} className={`bg-paper rounded-2xl p-5 grid md:grid-cols-[1fr_auto] gap-4 ${t.visible ? "" : "opacity-70"}`} data-testid={`admin-testimonial-${t.id}`}>
            <div>
              <p className="font-display text-lg leading-snug">“{t.quote}”</p>
              <p className="mt-2 lbl text-slate">{t.name}{!t.visible && " · hidden"}</p>
            </div>
            <div className="flex items-start gap-1">
              <button onClick={() => toggle(t)} aria-label={t.visible ? `Hide testimonial from ${t.name}` : `Show testimonial from ${t.name}`} className="p-2 rounded-full hover:bg-sky" data-testid={`testimonial-toggle-${t.id}`}>{t.visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              <button onClick={() => setEditing(t.id)} aria-label={`Edit testimonial from ${t.name}`} className="p-2 rounded-full hover:bg-sky" data-testid={`testimonial-edit-${t.id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(t)} aria-label={`Delete testimonial from ${t.name}`} className="p-2 rounded-full hover:bg-sky" data-testid={`testimonial-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
